/**
 * Export/import envelope — Persistence layer contract (STEP 4).
 * Round-trip must be lossless; import validates format + version before use.
 *
 * Validation is strict about what a *campaign* must carry (identity + the
 * collections every view reads unconditionally) and tolerant about what later
 * phases added: a compatible file that predates a collection loads with that
 * collection defaulted to empty. A failure always names the offending field.
 */
import type { Campaign } from './campaign';

export const CAMPAIGN_FILE_FORMAT = 'atla-gm-campaign' as const;
export const CAMPAIGN_FILE_VERSION = 1 as const;
export const CAMPAIGN_FILE_EXTENSIONS = ['.atlagm', '.json'] as const;

export interface CampaignAsset {
  id: string;
  mime: string;
  /** Inline assets (maps/portraits) travel with the file — local-first. */
  dataBase64: string;
}

export interface CampaignFile {
  format: typeof CAMPAIGN_FILE_FORMAT;
  version: typeof CAMPAIGN_FILE_VERSION;
  exportedAt: string;
  appVersion: string;
  campaign: Campaign;
  assets?: CampaignAsset[];
}

/**
 * Typed validation failure. `key` is the i18n key of a message that names
 * the problem (both dictionaries carry it); `field` is the offending field,
 * interpolated into the message's {field} placeholder, so the user reads
 * *why* the file was refused — never a bare "error". `message` keeps both
 * parts (`key: field`) for logs and tests.
 */
export class CampaignFileError extends Error {
  readonly key: string;
  readonly field: string | undefined;

  constructor(key: string, field?: string) {
    super(field !== undefined ? `${key}: ${field}` : key);
    this.name = 'CampaignFileError';
    this.key = key;
    this.field = field;
  }
}

/**
 * Collections every view reads unconditionally (domain/campaign.ts) — a file
 * without one is not a campaign, and adopting it would white-screen a view.
 */
const REQUIRED_COLLECTIONS = [
  'sessions',
  'characters',
  'npcs',
  'encounters',
  'maps',
  'notes',
] as const;

/**
 * Collections added by later phases — a compatible older file may simply not
 * have them. Missing → defaulted to []; present but not an array → refused.
 */
const OPTIONAL_COLLECTIONS = [
  'enemies',
  'locations',
  'factions',
  'items',
  'storyNodes',
  // GM screen pins (Phase 6): a compatible older file simply has none.
  'pinnedGmPanels',
] as const;

/** Missing required field → failure whose message names `campaign.<field>`. */
function requirePresent(c: Record<string, unknown>, field: string): unknown {
  const value = c[field];
  if (value === undefined || value === null) {
    throw new CampaignFileError('import.error.missingField', `campaign.${field}`);
  }
  return value;
}

function requireString(c: Record<string, unknown>, field: string): string {
  const value = requirePresent(c, field);
  if (typeof value !== 'string') {
    throw new CampaignFileError('import.error.invalidField', `campaign.${field}`);
  }
  return value;
}

/** Validate an untrusted parsed value (e.g. from a chosen file). */
export function parseCampaignFile(value: unknown): CampaignFile {
  if (typeof value !== 'object' || value === null) {
    throw new CampaignFileError('import.error.notObject');
  }
  const v = value as Record<string, unknown>;
  if (v.format !== CAMPAIGN_FILE_FORMAT) {
    throw new CampaignFileError('import.error.wrongFormat', String(v.format));
  }
  if (v.version !== CAMPAIGN_FILE_VERSION) {
    throw new CampaignFileError('import.error.unsupportedVersion', String(v.version));
  }
  if (typeof v.appVersion !== 'string') {
    throw new CampaignFileError('import.error.missingField', 'appVersion');
  }
  if (typeof v.exportedAt !== 'string') {
    throw new CampaignFileError('import.error.missingExportedAt');
  }
  const campaign = v.campaign;
  if (typeof campaign !== 'object' || campaign === null) {
    throw new CampaignFileError('import.error.missingCampaign');
  }
  const c = campaign as Record<string, unknown>;

  // ---- required top-level campaign fields (Campaign in domain/campaign.ts)
  if (requireString(c, 'id') === '') {
    throw new CampaignFileError('import.error.invalidField', 'campaign.id');
  }
  requireString(c, 'name');
  requireString(c, 'synopsis');
  if (!('era' in c) || c.era === undefined) {
    throw new CampaignFileError('import.error.missingField', 'campaign.era');
  }
  if (typeof c.era !== 'string' && c.era !== null) {
    throw new CampaignFileError('import.error.invalidField', 'campaign.era');
  }
  const ruleSystem = requirePresent(c, 'ruleSystem');
  if (ruleSystem !== 'fate' && ruleSystem !== 'dnd5e-atla' && ruleSystem !== 'mixed') {
    throw new CampaignFileError('import.error.unknownRuleSystem', String(ruleSystem));
  }

  // ---- required collections must exist …
  for (const key of REQUIRED_COLLECTIONS) {
    requirePresent(c, key);
  }
  // ---- … and every known collection must be an array when present at all
  // (forward tolerance: unknown extra keys are ignored, never rejected).
  const knownCollections: readonly string[] = [
    ...REQUIRED_COLLECTIONS,
    ...OPTIONAL_COLLECTIONS,
  ];
  for (const key of knownCollections) {
    const arr = c[key];
    if (arr !== undefined && !Array.isArray(arr)) {
      throw new CampaignFileError('import.error.invalidCollection', `campaign.${key}`);
    }
  }
  if (
    c.battle !== undefined &&
    c.battle !== null &&
    (typeof c.battle !== 'object' || Array.isArray(c.battle))
  ) {
    throw new CampaignFileError('import.error.invalidField', 'campaign.battle');
  }

  // ---- legacy tolerance: a compatible file that merely predates a later
  // phase's collection loads with that collection empty (never an error).
  const defaults: Record<string, unknown> = {};
  for (const key of OPTIONAL_COLLECTIONS) {
    if (c[key] === undefined) defaults[key] = [];
  }
  const normalizedCampaign = { ...c, ...defaults } as unknown as Campaign;

  return { ...v, campaign: normalizedCampaign } as CampaignFile;
}

export function serializeCampaignFile(file: CampaignFile): string {
  return JSON.stringify(file, null, 2);
}
